<script lang="ts">
import { getSchedule, postReaction } from './services';
import OpenChatRoom from './OpenChatRoom.svelte';
import LivePoll from './LivePoll.svelte';
import ReactionRiver from './ReactionRiver.svelte';
import { markdown } from './util';
import { AskQuestionEnabled, LivePollEnabled, ReactionsEnabled } from './theme';
import throttle from 'lodash.throttle';
import CircularProgress from './CircularProgress.svelte';
import { getChannel, sendBird } from './store';
import { onDestroy } from 'svelte';

export let id;
let loading = true;
let show;
let error;
let url;
let isPast;
let showChat = false;
let showReactions = false;
let river;
let chatEnabled = false;
let askQuestionChannel;
let presentQuestionDialog = false;
let question = '';
let closeQuestionTimer;
let channelSubscription;

enum QuestionState {
  Idle,
  Sending,
  Sent
}
let questionState = QuestionState.Idle;

const reactions = {
  'c': '<img src="/img/icon-clap.svg" />',
  'r': '<img src="/img/icon-cheers.svg" />',
  'h': '<img src="/img/icon-heart.svg" />',
}

const throttledPostReaction = throttle(postReaction, 1000, {trailing: false})
function sendReaction(id, emoji) {
  throttledPostReaction(id, emoji);
  river.add(emoji, true);
}

function getStreamUrl(stream) {
  switch (stream.type) {
    case 'livestream': return `${stream.url}/player?width=640&height=360&enableInfoAndActivity=true&defaultDrawer=&autoPlay=true&mute=false`;
    case 'vimeo': return `https://player.vimeo.com/video/${stream.url}?autoplay=1&loop=1&autopause=0`;
    case 'dacast': return stream.url;
    case 'ibm': return `https://video.ibm.com/embed/recorded/${stream.url}?autoplay=true&showtitle=false`;
  }
}

async function loadShow(id) {
  try {
    loading = true;

    const schedule = await getSchedule();
    show = schedule.find(s => s.id === id);
    if (!show) {
      error = "Show not found";
      return;
    }

    isPast = show.endTime <= new Date();
    if (isPast && !show.replayStream) {
      error = "Show has ended";
      return;
    }

    url = getStreamUrl(isPast ? show.replayStream : show.liveStream);
    if (!url) {
      error = "Unknown stream type";
    }

    chatEnabled = !!show.chatChannel;
    showReactions = ReactionsEnabled;
    if (AskQuestionEnabled) {
      channelSubscription?.unsubscribe();
      channelSubscription = getChannel("sendbird_open_channel_8736_064e6bf2bdcfe2f9b52029f5adc893946c53662d").subscribe(channel => askQuestionChannel = channel)
    }
  } catch (e) {
    console.error(e)
    error = "Unable to load specified show";
  } finally {
    loading = false;
  }
}

$: loadShow(id)

function closeQuestionDialog() {
  clearTimeout(closeQuestionTimer);
  presentQuestionDialog = false;
  question = ''
  questionState = QuestionState.Idle;
}

function sendQuestion(e) {
  e.preventDefault();

  const quest = question.trim();
  if (quest.length === 0) return;

  questionState = QuestionState.Sending;

  const params = new sendBird.UserMessageParams();
  params.message = `(${show.title}) ${quest}`;
  params.pushNotificationDeliveryOption = 'suppress';
  askQuestionChannel.sendUserMessage(params, (userMessage, error) => {
    if (!error) {
      question = ''
      questionState = QuestionState.Sent;

      closeQuestionTimer = setTimeout(closeQuestionDialog, 2000)
    } else {
      console.error(error)
      closeQuestionDialog()
    }
  });
}

onDestroy(() => {
  clearTimeout(closeQuestionTimer);
  channelSubscription?.unsubscribe();
})
</script>

<div class="row">
  <div class="section-header">MAIN STAGE</div>
</div>

{#if error}
  <h1 style="color: red">Error: {error}</h1>
{:else}
  <div class="rowtocol">
    <div class="sixteen-nine f1" style="max-height: 70vh">
      {#if show}
        <iframe
          title="show"
          src={url}
          width="100%"
          height="100%"
          frameborder="0"
          scrolling="no"
          allow="autoplay"
          allowfullscreen="true"
          webkitallowfullscreen="true"
          mozallowfullscreen="true"
          oallowfullscreen="true"
          msallowfullscreen="true"
          class="content" />
      {/if}

      {#if showReactions}
        <div class="reaction-emitter">
          <ReactionRiver bind:this={river} showId={show.id} reactionMapping={reactions} />
        </div>
      {/if}

    </div>
    {#if chatEnabled && !isPast && showChat}
      <div class="chatRoomBox col">
        <div class="topbar row vcenter between">
          <div class="control-button row vcenter hcenter" on:click={() => showChat = true}>
            <span class="material-icons" on:click={() => showChat = false}>close</span>
          </div>

          Live Chat

          <div style="width: 42px" />
        </div>
        <OpenChatRoom url={show.chatChannel} />
      </div>
    {/if}
  </div>

  {#if LivePollEnabled && show}
    <LivePoll showId={show.id} />
  {/if}

  <div class="title-box rowtocol-tablet vcenter between">
    <div class="row vcenter f1">
      <!-- <div class="whitebox" /> -->
      <!-- <div class="logo" />
      <div class="slant" /> -->
      <div class="title-text-1">2022 iWeek</div>
      <div class="title-text-2">MAIN STAGE</div>
    </div>

    <div class="row vcenter">
      {#if askQuestionChannel && !isPast}
        <div class="col hcenter mr-32">
          <div class='control-button col vcenter hcenter' on:click={() => presentQuestionDialog = true}>
            <img src="/img/icon-bullhorn.svg" alt="Ask a Question" />
          </div>
          <div class="control-label">Ask a Question</div>
        </div>
      {/if}
      
      {#if showReactions}
        <div class="col hcenter mr-32">
          <div class="row">
            {#each Object.entries(reactions) as [key, val]}
              <div class="control-button col vcenter hcenter" on:click={() => sendReaction(id, key)}>{@html val}</div>
            {/each}
          </div>
          <div class="control-label">Reactions</div>
        </div>
      {/if}

      {#if chatEnabled && !isPast}
        <div class="col hcenter">
          <div class="control-button col vcenter hcenter" on:click={() => showChat = !showChat}>
            <img src="/img/icon-chat.svg" alt="Chat" />
          </div>
          <div class="control-label">Chat</div>
        </div>
      {/if}
    </div>
  </div>

  {#if show}
    <div class="about-box col">
      <div class="title">ABOUT</div>
      <div class="sub-title">{show.title}</div>
      <div class="desc">{@html markdown(show.description)}</div>
    </div>
  {/if}
{/if}

{#if presentQuestionDialog}
  <div class="modal">
    <div class="dialog-container col" class:vcenter={questionState === QuestionState.Sent}>

      {#if questionState !== QuestionState.Sent}
        <div class='row between vcenter mb-8'>
          <div class="dialog-title">Ask a Question</div>
          <div class="control-button col vcenter hcenter" on:click={closeQuestionDialog}><i class="material-icons">close</i></div>
        </div>
        <div class="dialog-label">Question:</div>
        <textarea class="question-text mb-8" bind:value={question} disabled={questionState === QuestionState.Sending} />
        {#if questionState === QuestionState.Sending}
          <CircularProgress />
        {:else}
          <div class="button btn" style="align-self: flex-end" on:click={sendQuestion}>Submit Question</div>
        {/if}
      {:else}
        <div class="question-sent">Sent!</div>
      {/if}
    </div>
  </div>
{/if}

<style lang="scss">
@import './util';
@import './theme';

.dialog-container {
  width: 400px;
  height: 250px;
  border-radius: 5px;
  background-color: #373836;
  color: white;
  @include shadow;
  padding: 8px 16px;
}

.dialog-title {
  font-size: 24px;
}

.dialog-label {
  font-size: 18px;
}

.question-text {
  width: 100%;
  height: 80px;
}

.question-sent {
  align-self: center;
  font-size: 26px;
  font-weight: bold;
}

.control-label {
  user-select: none;
  font-size: 14px;
  color: white;
  opacity: .75;
}

.reaction-emitter {
  pointer-events: none;
  bottom: 35px;
  right: 180px;
  position: absolute;
  z-index: 1000;
}
.chatRoomBox {
  display: flex;
  border: 1px solid rgba(0,0,0,.1);
  min-width: 280px;
  background-color: #f7f7f8;

  @include mobile {
    height: 40vh;
  }
}

.topbar {
  height: 42px;
  border-bottom: 1px solid rgba(0,0,0,.1);
  background-color: white;
}

$control-button-size: 42px;

.control-button {
  user-select: none;
  cursor: pointer;
  border-radius: 5px;
  width: $control-button-size;
  height: $control-button-size;
  &:hover {
    background-color: rgba(255,255,255,.2);
  }
}

.title-box {
  background-color: $show-box-background-color;
  color: $show-box-foreground-color;
  font-size: 32px;
  padding: 16px 32px;
  flex-shrink: 0;
}

.whitebox {
  width: 12px;
  height: 100%;
  background-color: white;
}

.title-text-1 {
  color: white;
  white-space: nowrap;
  font-size: 32px;
  font-weight: 900;
}
.title-text-2 {
  color: black;
  font-size: 24px;
  font-weight: normal;
  margin-left: 24px;
}

.about-box {
  @include shadow;
  background-color: $show-box-background-color;
  color: $show-box-foreground-color;
  margin: 16px 0;
  padding: 32px 64px;
}

.title {
  color: $primary-color;
  margin-bottom: 8px;
  font-size: 20px;
  font-weight: bold;
}

.sub-title {
  font-family: $secondary-font;
  // color: black;
  margin-bottom: 8px;
  font-weight: bold;
  font-size: 16px;
}

.desc {
  font-size: 16px;
  font-weight: normal;
  font-family: $secondary-font;
}

.sixteen-nine {
  @include aspect-ratio(16, 9);
  background-color: black;
  overflow: hidden;
}
</style>